import { useState } from 'react';

import { keyBy, uniqBy } from 'lodash';

import { useTopEntertainersQuery } from '@queries/EntertainerQueries';
import { CurrentPromoter } from '@queries/PromoterQueries';

import { useCreatePromoterMutation, useUpdatePromoterMutation } from '@mutations/PromoterMutations';

import { CurrentCity } from '@hooks/useCurrentUserLocation';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Textarea } from '@components/form/Textarea';

import { CitiesField, MultiSelectField } from '@screens/entertainer/components/CitiesField';

import './PromoterForm.scss';

export const PromoterForm = ({
  currentCity,
  cities,
  promoter,
  onSave,
}: {
  currentCity?: CurrentCity;
  cities: CurrentCity[];
  promoter?: CurrentPromoter;
  onSave?: () => void;
}) => {
  const [avatar, setAvatar] = useState<any>();
  const [title, setTitle] = useState(promoter?.title ?? '');
  const [description, setDescription] = useState(promoter?.description ?? '');
  const [cityIds, setCityIds] = useState<string[]>(
    promoter ? promoter.cityIds : currentCity?._id ? [currentCity._id] : [],
  );
  const [entertainers, setEntertainers] = useState<
    Record<
      string,
      {
        role?: string;
        weight?: number;
      }
    >
  >(keyBy(promoter?.entertainers ?? [], '_id') ?? {});

  const { isSubmitting, createPromoterAsync } = useCreatePromoterMutation();
  const { isSubmitting: isUpdatingProfile, updatePromoterAsync } = useUpdatePromoterMutation();

  const { topEntertainersAreReady, topEntertainers = [] } = useTopEntertainersQuery(
    cityIds[0],
    'dragPerformer',
    true,
  );

  const handleCreatePromoter = async () => {
    if (promoter) {
      await updatePromoterAsync({
        promoterId: promoter._id,
        avatar,
        title,
        description,
        cityIds,
        entertainers,
      });
    } else {
      await createPromoterAsync({
        avatar,
        title,
        description,
        cityIds,
        entertainers,
      });
    }

    onSave?.();
  };

  const handleUpdateEntertainers = (updatedEntertainerIds: string[]) => {
    setEntertainers((prevValue) => {
      return updatedEntertainerIds.reduce(
        (acc, entertainerId) => {
          acc[entertainerId] = {
            role: prevValue[entertainerId]?.role,
            weight: prevValue[entertainerId]?.weight,
          };
          return acc;
        },
        {} as Record<string, { role?: string; weight?: number }>,
      );
    });
  };

  return (
    <div className="promoter-form grid gap-2">
      <div>
        <div
          className="py-2 px-3"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 8 }}
        >
          <ImageUploadField
            defaultValue={promoter?.avatarUrl}
            placeholder={<span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Select image</span>}
            imgStyle={{ width: '100%', height: 200, objectFit: 'contain', margin: '16px 0' }}
            onChange={setAvatar}
          />
        </div>
      </div>

      <div>
        <FormLabel text="Organization Name *" />
        <Input
          placeholder="eg. Glitter Disco, Beyond Binary, etc."
          defaultValue={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div>
        <FormLabel text="Description *" />
        <Textarea
          placeholder="Tell everyone about what you do"
          defaultValue={description}
          onChange={setDescription}
        />
      </div>

      <CitiesField cities={cities} cityIds={cityIds} onChange={setCityIds} />

      <MultiSelectField
        key={
          topEntertainersAreReady
            ? `top-entertainers-${cityIds[0]}`
            : `top-entertainers-${cityIds[0]}-loading`
        }
        label={'Performers'}
        name="entertainerIds"
        allowSearch
        defaultValue={Object.keys(entertainers)}
        options={uniqBy([...topEntertainers, ...(promoter?.entertainers ?? [])], '_id')
          .map(({ _id, title }) => ({
            value: _id,
            label: title ?? _id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))}
        onChange={handleUpdateEntertainers}
      />

      <div className="mt-2">
        <Button
          roundness="rounded"
          disabled={!title || !description || !cityIds.length || isSubmitting || isUpdatingProfile}
          onClick={handleCreatePromoter}
        >
          Save Profile
        </Button>
      </div>
    </div>
  );
};
